import React, { useRef, useState, useEffect } from 'react';
import { RotateCcw, CheckCircle, Edit3, Type, Sparkles } from 'lucide-react';

interface AssinaturaDigitalPadProps {
  onSignatureChange: (signatureBase64: string | null, nomeSignatario: string, documentoSignatario: string) => void;
  initialNome?: string;
  initialDoc?: string;
  label?: string;
  required?: boolean;
}

export function AssinaturaDigitalPad({
  onSignatureChange,
  initialNome = '',
  initialDoc = '',
  label = 'Assinatura Digital do Solicitante / Cliente',
  required = false
}: AssinaturaDigitalPadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [mode, setMode] = useState<'draw' | 'type'>('draw');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [typedName, setTypedName] = useState(initialNome);
  const [documento, setDocumento] = useState(initialDoc);
  const [cargoFuncao, setCargoFuncao] = useState('');

  // Initialize canvas
  useEffect(() => {
    if (mode === 'draw') {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.strokeStyle = '#0f172a';
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
        }
      }
    }
  }, [mode]);

  // Handle Typed Signature rendering to canvas
  useEffect(() => {
    if (mode === 'type' && typedName.trim()) {
      const canvas = document.createElement('canvas');
      canvas.width = 500;
      canvas.height = 150;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        ctx.font = 'italic bold 32px "Caveat", "Brush Script MT", "Segoe Script", cursive, sans-serif';
        ctx.fillStyle = '#0f172a';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedName.trim(), canvas.width / 2, canvas.height / 2 - 10);

        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#64748b';
        ctx.fillText(`Assinado digitalmente por ${typedName} - Doc: ${documento || 'Não informado'}`, canvas.width / 2, canvas.height / 2 + 35);
        ctx.fillText(`Data: ${new Date().toLocaleString('pt-BR')}`, canvas.width / 2, canvas.height / 2 + 52);

        const base64 = canvas.toDataURL('image/png');
        onSignatureChange(base64, typedName, documento);
      }
    } else if (mode === 'type' && !typedName.trim()) {
      onSignatureChange(null, '', documento);
    }
  }, [mode, typedName, documento]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const x = ((clientX - rect.left) / rect.width) * canvas.width;
    const y = ((clientY - rect.top) / rect.height) * canvas.height;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const x = ((clientX - rect.left) / rect.width) * canvas.width;
    const y = ((clientY - rect.top) / rect.height) * canvas.height;

    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas && hasDrawn) {
      const base64 = canvas.toDataURL('image/png');
      onSignatureChange(base64, typedName, documento);
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setHasDrawn(false);
    onSignatureChange(null, typedName, documento);
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
        <div className="flex items-center space-x-2">
          <Edit3 className="w-4 h-4 text-red-600" />
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            {label} {required && <span className="text-red-500">*</span>}
          </h2>
        </div>

        <div className="flex items-center bg-slate-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setMode('draw')}
            className={`px-3 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
              mode === 'draw'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Edit3 className="w-3.5 h-3.5" />
            <span>Desenhar na Tela</span>
          </button>
          <button
            type="button"
            onClick={() => setMode('type')}
            className={`px-3 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
              mode === 'type'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Type className="w-3.5 h-3.5" />
            <span>Digitar Nome</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 mb-1">
            Nome do Responsável / Solicitante <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={typedName}
            onChange={(e) => {
              setTypedName(e.target.value);
              if (mode === 'draw' && hasDrawn) {
                const canvas = canvasRef.current;
                if (canvas) {
                  onSignatureChange(canvas.toDataURL('image/png'), e.target.value, documento);
                }
              }
            }}
            placeholder="Nome completo de quem está assinando"
            className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-700 mb-1">
            Documento (CPF / RG)
          </label>
          <input
            type="text"
            value={documento}
            onChange={(e) => {
              setDocumento(e.target.value);
              if (mode === 'draw' && hasDrawn) {
                const canvas = canvasRef.current;
                if (canvas) {
                  onSignatureChange(canvas.toDataURL('image/png'), typedName, e.target.value);
                }
              }
            }}
            placeholder="000.000.000-00 ou RG"
            className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-700 mb-1">
            Cargo / Departamento
          </label>
          <input
            type="text"
            value={cargoFuncao}
            onChange={(e) => setCargoFuncao(e.target.value)}
            placeholder="Ex: Gerente de Logística, Compras..."
            className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>

      {mode === 'draw' ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-500">
              Desenhe sua assinatura no quadro abaixo usando o mouse ou dedo no celular/tablet:
            </span>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1 p-1 hover:bg-red-50 rounded-lg transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Limpar</span>
            </button>
          </div>

          <div className="relative border-2 border-dashed border-slate-300 hover:border-slate-400 bg-slate-50/50 rounded-2xl overflow-hidden touch-none h-36 flex items-center justify-center">
            <canvas
              ref={canvasRef}
              width={600}
              height={144}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className="w-full h-full cursor-crosshair"
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-xs">
                <span className="bg-white/80 px-3 py-1.5 rounded-full border border-slate-200 shadow-xs">
                  ✍️ Clique ou toque aqui para assinar
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl text-center space-y-2">
          <span className="text-[11px] font-bold text-slate-500 uppercase">Prévia da Assinatura Eletrônica</span>
          <div className="bg-white p-4 rounded-xl border border-slate-200 max-w-md mx-auto shadow-xs">
            <p className="text-2xl italic font-serif text-slate-900">
              {typedName.trim() || 'Seu Nome Aqui'}
            </p>
            <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400">
              <span>Assinatura Digital Validade Jurídica MP 2.200-2/2001</span>
            </div>
          </div>
        </div>
      )}

      <div className="p-3 bg-emerald-50/60 border border-emerald-200 rounded-xl flex items-start gap-2 text-xs text-emerald-900">
        <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <span className="font-bold">Validação de Autenticidade:</span> Ao assinar, você confirma a autorização para execução dos serviços solicitados sob os termos do contrato comercial vigente.
        </div>
      </div>
    </div>
  );
}
